/** Local capture types + seed data for Procurement Supplier governance tabs
 * (ESG, Certifications & Insurance, Contract & Renewal, Delivery Rules, Price Lists,
 * Approval & Rebate, Performance & Data Quality). UI-only for the demo - no backend
 * persistence exists yet on MarketSupplier, mirrors the ProductForm allergens/ingredients
 * local-only tab precedent. Keyed by supplierId (falls back to a generated default profile
 * for any supplier not explicitly seeded below). */

export enum SupplierApprovalStatus {
  PendingReview = 'PendingReview',
  Approved = 'Approved',
  AutoApprovedLowVolume = 'AutoApprovedLowVolume',
  Rejected = 'Rejected',
  Suspended = 'Suspended',
}

export enum SupplierOnboardingTier {
  Standard = 'Standard',
  QuickOnboard = 'QuickOnboard',
}

/** Supplier Onboarding wizard type/tier (dedicated wizard only - see
 * src/pages/Procurement/Suppliers/Onboarding/index.tsx). Distinct from the legacy
 * `SupplierOnboardingTier` above, which drives SupplierForm's Governance tab quick-onboard
 * switch and is left untouched. Each type determines which wizard steps are shown, ordered
 * roughly by how much governance evidence is required. */
export enum SupplierOnboardingType {
  Full = 'Full',
  ApprovedLocal = 'ApprovedLocal',
  OneOff = 'OneOff',
  Micro = 'Micro',
  Trial = 'Trial',
}

export const SUPPLIER_ONBOARDING_TYPE_ORDER: SupplierOnboardingType[] = [
  SupplierOnboardingType.Full,
  SupplierOnboardingType.ApprovedLocal,
  SupplierOnboardingType.OneOff,
  SupplierOnboardingType.Micro,
  SupplierOnboardingType.Trial,
];

export const SUPPLIER_ONBOARDING_TYPE_LABELS: Record<SupplierOnboardingType, string> = {
  [SupplierOnboardingType.Full]: 'Full supplier',
  [SupplierOnboardingType.ApprovedLocal]: 'Approved local supplier',
  [SupplierOnboardingType.OneOff]: 'One-off supplier',
  [SupplierOnboardingType.Micro]: 'Micro supplier',
  [SupplierOnboardingType.Trial]: 'Trial supplier',
};

export const SUPPLIER_ONBOARDING_TYPE_DESCRIPTIONS: Record<SupplierOnboardingType, string> = {
  [SupplierOnboardingType.Full]: 'Full due diligence, delivery & pricing evidence, ongoing contract/renewal tracking. Required for higher-risk or higher-spend suppliers.',
  [SupplierOnboardingType.ApprovedLocal]: 'Locally sourced, low-risk supplier signed off by a site/area owner. Basic details only, no formal due diligence pack.',
  [SupplierOnboardingType.OneOff]: 'Single, non-recurring purchase. Basic details captured for traceability, no ongoing governance expected.',
  [SupplierOnboardingType.Micro]: 'Very small/low-spend supplier used occasionally. Basic details only, auto-approved at low volume.',
  [SupplierOnboardingType.Trial]: 'Time-boxed trial ahead of a full commitment. Basic details plus a mandatory review/expiry date.',
};

/** Basic capture fields required for every onboarding type, regardless of how lightweight
 * the wizard path is - Full onboarding captures these too, on top of the fuller Due
 * Diligence / Delivery & Pricing sections. */
export interface SupplierOnboardingBasicDetails {
  supplierName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  siteLocationsServed: string;
  productRangeSupplied: string;
  approvalOwner: string;
  allergenResponsibility: SupplierAllergenResponsibility | '';
  paymentInvoiceMethod: SupplierPaymentInvoiceMethod | '';
  deliveryMethod: SupplierDeliveryMethod | '';
  expiryReviewDate: string;
}

export const EMPTY_SUPPLIER_ONBOARDING_BASIC_DETAILS: SupplierOnboardingBasicDetails = {
  supplierName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  siteLocationsServed: '',
  productRangeSupplied: '',
  approvalOwner: '',
  allergenResponsibility: '',
  paymentInvoiceMethod: '',
  deliveryMethod: '',
  expiryReviewDate: '',
};

export type SupplierAllergenResponsibility = 'Supplier' | 'Site' | 'Shared';
export type SupplierPaymentInvoiceMethod = 'BACS' | 'Card' | 'Cash' | 'Invoice30' | 'Invoice60';
export type SupplierDeliveryMethod = 'SupplierDelivery' | 'Collection' | 'ThirdPartyCourier';

export const SUPPLIER_ALLERGEN_RESPONSIBILITY_LABELS: Record<SupplierAllergenResponsibility, string> = {
  Supplier: 'Supplier confirms allergen data',
  Site: 'Site/store confirms allergen data',
  Shared: 'Shared responsibility',
};

export const SUPPLIER_PAYMENT_INVOICE_METHOD_LABELS: Record<SupplierPaymentInvoiceMethod, string> = {
  BACS: 'BACS transfer',
  Card: 'Card payment',
  Cash: 'Cash on delivery',
  Invoice30: 'Invoice - 30 day terms',
  Invoice60: 'Invoice - 60 day terms',
};

export const SUPPLIER_DELIVERY_METHOD_LABELS: Record<SupplierDeliveryMethod, string> = {
  SupplierDelivery: 'Supplier delivers direct',
  Collection: 'Collection by site/store',
  ThirdPartyCourier: 'Third-party courier',
};

/** Conditions that mean a supplier must go through Full onboarding regardless of the type
 * initially selected - per the "when to escalate to full onboarding" governance rule. */
export interface SupplierFullOnboardingTriggerCriteria {
  spendExceedsThreshold: boolean;
  usedByMultipleSites: boolean;
  highRiskFoodCategory: boolean;
  recurringOrders: boolean;
  clientRequiresEvidence: boolean;
  esgComplianceApplies: boolean;
}

export const EMPTY_SUPPLIER_FULL_ONBOARDING_TRIGGER_CRITERIA: SupplierFullOnboardingTriggerCriteria = {
  spendExceedsThreshold: false,
  usedByMultipleSites: false,
  highRiskFoodCategory: false,
  recurringOrders: false,
  clientRequiresEvidence: false,
  esgComplianceApplies: false,
};

const SUPPLIER_FULL_ONBOARDING_TRIGGER_REASONS: Record<keyof SupplierFullOnboardingTriggerCriteria, string> = {
  spendExceedsThreshold: 'Annual spend with this supplier exceeds the approval threshold.',
  usedByMultipleSites: 'Supplier is used by more than one site.',
  highRiskFoodCategory: 'Supplier supplies a high-risk food category.',
  recurringOrders: 'Supplier is used for recurring (not one-off) orders.',
  clientRequiresEvidence: 'Client contractually requires supplier due diligence evidence.',
  esgComplianceApplies: 'ESG/compliance requirements apply to this supplier.',
};

/** Returns the human-readable reasons Full onboarding is required, based on which trigger
 * criteria are set. Empty array means none of the escalation triggers apply. */
export function evaluateFullOnboardingTriggers(criteria: SupplierFullOnboardingTriggerCriteria): string[] {
  return (Object.keys(SUPPLIER_FULL_ONBOARDING_TRIGGER_REASONS) as (keyof SupplierFullOnboardingTriggerCriteria)[])
    .filter((key) => criteria[key])
    .map((key) => SUPPLIER_FULL_ONBOARDING_TRIGGER_REASONS[key]);
}

/** Wizard steps to show for a given onboarding type - only Full walks through the fuller
 * Due Diligence / Delivery & Pricing governance sections; every other type is basic-details
 * capture only, gated by the escalation criteria check. */
export const SUPPLIER_ONBOARDING_TYPE_INCLUDES_FULL_GOVERNANCE: Record<SupplierOnboardingType, boolean> = {
  [SupplierOnboardingType.Full]: true,
  [SupplierOnboardingType.ApprovedLocal]: false,
  [SupplierOnboardingType.OneOff]: false,
  [SupplierOnboardingType.Micro]: false,
  [SupplierOnboardingType.Trial]: false,
};

export type DocumentStatus = 'Valid' | 'ExpiringSoon' | 'Expired';
export type RenewalStatus = 'OnTrack' | 'RenewalDueSoon' | 'Overdue';
export type PerformanceTrend = 'Up' | 'Down' | 'Flat';

export interface SupplierEsgInfo {
  carbonRatingScore?: number;
  ethicalSourcingStatement?: string;
  modernSlaveryStatementUrl?: string;
  sustainabilityAccreditations: string[];
  lastReviewedDate?: string;
}

export interface SupplierCertification {
  id: number;
  name: string;
  issuingBody: string;
  certificateNumber: string;
  issueDate: string;
  expiryDate: string;
  status: DocumentStatus;
}

export interface SupplierInsurancePolicy {
  id: number;
  type: 'PublicLiability' | 'ProductLiability' | 'EmployersLiability';
  provider: string;
  policyNumber: string;
  coverAmount: number;
  expiryDate: string;
  status: DocumentStatus;
}

/** Onboarding/compliance paperwork tracked separately from certifications & insurance
 * (e.g. signed agreements, food safety policy, bank details form) - each just needs a
 * received/outstanding state plus an optional expiry, not the fuller certificate/policy
 * evidence captured elsewhere. */
export interface SupplierRequiredDocument {
  id: number;
  name: string;
  category: 'Legal' | 'FoodSafety' | 'Financial' | 'Insurance' | 'Other';
  received: boolean;
  receivedDate?: string;
  expiryDate?: string;
  status: DocumentStatus;
}

export interface SupplierContract {
  id: number;
  contractReference: string;
  startDate: string;
  endDate: string;
  renewalNoticePeriodDays: number;
  autoRenew: boolean;
  renewalStatus: RenewalStatus;
}

export interface SupplierDeliveryRule {
  id: number;
  leadTimeDays: number;
  deliveryDays: string[];
  minimumOrderValue: number;
  assignedStoreNumbers: string[];
}

export interface SupplierPriceListEntry {
  id: number;
  itemNumber: string;
  itemName: string;
  price: number;
  effectiveFrom: string;
  effectiveTo?: string;
  pendingApproval?: boolean;
}

export interface SupplierRebateAgreement {
  id: number;
  periodLabel: string;
  rebatePercentage: number;
  thresholdSpend: number;
  accruedYtd: number;
}

export interface SupplierPerformanceMetrics {
  onTimeDeliveryPercentage: number;
  fillRatePercentage: number;
  qualityScore: number;
  scoreTrend: PerformanceTrend;
  lastIncidentDate?: string;
}

export interface SupplierDataQualityFlag {
  id: number;
  field: string;
  issue: string;
  severity: 'Info' | 'Warning' | 'Blocking';
}

export interface SupplierGovernanceProfile {
  supplierId: number;
  onboardingTier: SupplierOnboardingTier;
  approvalStatus: SupplierApprovalStatus;
  esg: SupplierEsgInfo;
  certifications: SupplierCertification[];
  insurancePolicies: SupplierInsurancePolicy[];
  requiredDocuments: SupplierRequiredDocument[];
  contract?: SupplierContract;
  deliveryRules: SupplierDeliveryRule[];
  priceList: SupplierPriceListEntry[];
  rebateAgreements: SupplierRebateAgreement[];
  performance?: SupplierPerformanceMetrics;
  dataQualityFlags: SupplierDataQualityFlag[];
  /** Set when this supplier record originated from a chef "refer to procurement" action. */
  referredByProduct?: string;
}

/** Seed profiles for demo suppliers, keyed by supplierId. */
export const supplierGovernanceProfiles: Record<number, SupplierGovernanceProfile> = {
  1: {
    supplierId: 1,
    onboardingTier: SupplierOnboardingTier.Standard,
    approvalStatus: SupplierApprovalStatus.Approved,
    esg: {
      carbonRatingScore: 82,
      ethicalSourcingStatement: 'Signed Ethical Trading Initiative base code, audited annually.',
      modernSlaveryStatementUrl: 'https://example.com/modern-slavery-statement.pdf',
      sustainabilityAccreditations: ['Red Tractor', 'RSPO'],
      lastReviewedDate: '2026-02-01',
    },
    certifications: [
      { id: 1, name: 'BRCGS Food Safety', issuingBody: 'BRC', certificateNumber: 'BRC-88213', issueDate: '2025-03-01', expiryDate: '2027-03-01', status: 'Valid' },
      { id: 2, name: 'ISO 22000', issuingBody: 'BSI', certificateNumber: 'ISO-40021', issueDate: '2024-06-01', expiryDate: '2026-08-15', status: 'ExpiringSoon' },
    ],
    insurancePolicies: [
      { id: 1, type: 'PublicLiability', provider: 'Aviva', policyNumber: 'PL-99123', coverAmount: 5000000, expiryDate: '2026-12-31', status: 'Valid' },
      { id: 2, type: 'ProductLiability', provider: 'Aviva', policyNumber: 'PROD-99124', coverAmount: 5000000, expiryDate: '2026-12-31', status: 'Valid' },
    ],
    requiredDocuments: [
      { id: 1, name: 'Signed supplier agreement', category: 'Legal', received: true, receivedDate: '2024-04-01', status: 'Valid' },
      { id: 2, name: 'Food safety policy', category: 'FoodSafety', received: true, receivedDate: '2025-03-01', expiryDate: '2027-03-01', status: 'Valid' },
      { id: 3, name: 'Bank details form', category: 'Financial', received: true, receivedDate: '2024-04-01', status: 'Valid' },
    ],
    contract: {
      id: 1, contractReference: 'CTR-2024-0451', startDate: '2024-04-01', endDate: '2026-09-30',
      renewalNoticePeriodDays: 60, autoRenew: false, renewalStatus: 'RenewalDueSoon',
    },
    deliveryRules: [
      { id: 1, leadTimeDays: 2, deliveryDays: ['Monday', 'Wednesday', 'Friday'], minimumOrderValue: 150, assignedStoreNumbers: ['0101', '0102', '0145'] },
    ],
    priceList: [
      { id: 1, itemNumber: 'FLR-001', itemName: 'Strong White Flour 16kg', price: 12.4, effectiveFrom: '2026-01-01' },
      { id: 2, itemNumber: 'BTR-002', itemName: 'Unsalted Butter Block 10kg', price: 38.9, effectiveFrom: '2026-04-01', pendingApproval: true },
    ],
    rebateAgreements: [
      { id: 1, periodLabel: 'FY26 Q2', rebatePercentage: 2.5, thresholdSpend: 20000, accruedYtd: 612.4 },
    ],
    performance: {
      onTimeDeliveryPercentage: 96.2, fillRatePercentage: 98.1, qualityScore: 4.6, scoreTrend: 'Up', lastIncidentDate: '2025-11-02',
    },
    dataQualityFlags: [],
  },
  2: {
    supplierId: 2,
    onboardingTier: SupplierOnboardingTier.Standard,
    approvalStatus: SupplierApprovalStatus.PendingReview,
    esg: {
      sustainabilityAccreditations: [],
    },
    certifications: [
      { id: 3, name: 'BRCGS Food Safety', issuingBody: 'BRC', certificateNumber: 'BRC-11029', issueDate: '2024-01-10', expiryDate: '2026-06-30', status: 'Expired' },
    ],
    insurancePolicies: [
      { id: 3, type: 'PublicLiability', provider: 'Zurich', policyNumber: 'PL-40021', coverAmount: 2000000, expiryDate: '2026-08-01', status: 'Valid' },
    ],
    requiredDocuments: [
      { id: 4, name: 'Signed supplier agreement', category: 'Legal', received: false, status: 'Expired' },
      { id: 5, name: 'Bank details form', category: 'Financial', received: true, receivedDate: '2025-11-04', status: 'Valid' },
    ],
    deliveryRules: [
      { id: 2, leadTimeDays: 3, deliveryDays: ['Tuesday', 'Thursday'], minimumOrderValue: 75, assignedStoreNumbers: ['0101'] },
    ],
    priceList: [],
    rebateAgreements: [],
    performance: {
      onTimeDeliveryPercentage: 81.4, fillRatePercentage: 88.0, qualityScore: 3.1, scoreTrend: 'Down', lastIncidentDate: '2026-06-20',
    },
    dataQualityFlags: [
      { id: 1, field: 'Certifications', issue: 'BRCGS Food Safety certificate expired 2026-06-30.', severity: 'Blocking' },
      { id: 2, field: 'ESG', issue: 'No sustainability accreditations on file.', severity: 'Warning' },
    ],
  },
};

/** Default governance profile for a brand-new/unseeded supplier (standard onboarding). */
export function createDefaultGovernanceProfile(supplierId: number): SupplierGovernanceProfile {
  return {
    supplierId,
    onboardingTier: SupplierOnboardingTier.Standard,
    approvalStatus: SupplierApprovalStatus.PendingReview,
    esg: { sustainabilityAccreditations: [] },
    certifications: [],
    insurancePolicies: [],
    requiredDocuments: [],
    deliveryRules: [],
    priceList: [],
    rebateAgreements: [],
    dataQualityFlags: [
      { id: 1, field: 'Certifications', issue: 'No certifications captured yet.', severity: 'Warning' },
      { id: 2, field: 'Insurance', issue: 'No insurance policies captured yet.', severity: 'Warning' },
    ],
  };
}

/** Minimal profile for the "tiny supplier" quick-onboarding fast path - skips most governance
 * tabs and auto-approves at low volume, per the demo's "good for super small suppliers" ask. */
export function createQuickOnboardGovernanceProfile(supplierId: number): SupplierGovernanceProfile {
  return {
    supplierId,
    onboardingTier: SupplierOnboardingTier.QuickOnboard,
    approvalStatus: SupplierApprovalStatus.AutoApprovedLowVolume,
    esg: { sustainabilityAccreditations: [] },
    certifications: [],
    insurancePolicies: [],
    requiredDocuments: [],
    deliveryRules: [],
    priceList: [],
    rebateAgreements: [],
    dataQualityFlags: [],
  };
}

export function getSupplierGovernanceProfile(supplierId: number): SupplierGovernanceProfile {
  return supplierGovernanceProfiles[supplierId] ?? createDefaultGovernanceProfile(supplierId);
}

/** Merges a patch into the supplier's governance profile, creating it from the default
 * profile on first edit (Demo Mode edit screen only - see
 * `Procurement/Suppliers/SupplierEditDemo/index.tsx`). */
export function updateSupplierGovernanceProfile(supplierId: number, patch: Partial<SupplierGovernanceProfile>): SupplierGovernanceProfile {
  const updated = { ...getSupplierGovernanceProfile(supplierId), ...patch };
  supplierGovernanceProfiles[supplierId] = updated;
  return updated;
}
